import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Camera, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Eye,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FaceVerification = ({ 
  open, 
  onOpenChange, 
  onVerificationComplete,
  livenessCheckEnabled = false 
}) => {
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [loadingModels, setLoadingModels] = useState(true);
  const [cameraActive, setCameraActive] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  
  // Liveness detection states
  const [livenessStep, setLivenessStep] = useState('waiting'); // 'waiting', 'blink', 'done'
  const [blinkDetected, setBlinkDetected] = useState(false);
  const [eyeOpenHistory, setEyeOpenHistory] = useState([]);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectionIntervalRef = useRef(null);

  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      setLoadingModels(true);
      
      // Get the base URL for models - try local first, then CDN fallback
      const localModelUrl = window.location.origin + '/models';
      const cdnModelUrl = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model';
      
      // Retry logic with CDN fallback
      const maxRetries = 2;
      let lastError = null;
      let modelUrl = localModelUrl;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`Loading face-api models (attempt ${attempt}/${maxRetries}) from: ${modelUrl}`);
          
          await Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl),
            faceapi.nets.faceLandmark68Net.loadFromUri(modelUrl),
            faceapi.nets.faceRecognitionNet.loadFromUri(modelUrl)
          ]);
          
          setModelsLoaded(true);
          console.log('Face-api models loaded for verification from:', modelUrl);
          lastError = null;
          break;
        } catch (error) {
          console.error(`Error loading face-api models (attempt ${attempt}) from ${modelUrl}:`, error);
          lastError = error;
          
          // On first failure, try CDN on next attempt
          if (attempt === 1) {
            modelUrl = cdnModelUrl;
            console.log('Falling back to CDN models...');
          }
        }
      }
      
      if (lastError) {
        toast.error('Failed to load face recognition models. Please try again later.');
      }
      
      setLoadingModels(false);
    };

    if (open) {
      loadModels();
    }
  }, [open]);

  // Auto-start camera when models are loaded
  useEffect(() => {
    if (modelsLoaded && open && !cameraActive) {
      startCamera();
    }
  }, [modelsLoaded, open]);

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' }
      });
      
      streamRef.current = stream;
      setCameraActive(true);
      
      // Wait for state update to render the video element
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          
          // Handle both cases: video already loaded or loading
          const handleVideoReady = () => {
            if (videoRef.current) {
              videoRef.current.play().catch(err => console.error('Video play error:', err));
              startFaceDetection();
            }
          };
          
          if (videoRef.current.readyState >= 2) {
            // Video is already ready
            handleVideoReady();
          } else {
            // Wait for video to be ready
            videoRef.current.onloadeddata = handleVideoReady;
          }
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Failed to access camera');
    }
  };

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    setCameraActive(false);
    setFaceDetected(false);
  }, []);

  // Calculate eye aspect ratio (EAR) for blink detection
  const calculateEAR = (eye) => {
    // Eye landmarks: 6 points forming the eye shape
    // EAR = (||p2-p6|| + ||p3-p5||) / (2 * ||p1-p4||)
    const p1 = eye[0];
    const p2 = eye[1];
    const p3 = eye[2];
    const p4 = eye[3];
    const p5 = eye[4];
    const p6 = eye[5];
    
    const dist = (a, b) => Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2));
    
    const vertical1 = dist(p2, p6);
    const vertical2 = dist(p3, p5);
    const horizontal = dist(p1, p4);
    
    return (vertical1 + vertical2) / (2 * horizontal);
  };

  // Face detection loop with liveness check
  const startFaceDetection = () => {
    if (livenessCheckEnabled) {
      setLivenessStep('blink');
    }
    
    detectionIntervalRef.current = setInterval(async () => {
      if (videoRef.current && videoRef.current.readyState === 4) {
        const detection = await faceapi
          .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceDescriptor();
        
        if (detection) {
          setFaceDetected(true);
          
          // Draw detection
          if (canvasRef.current) {
            const dims = faceapi.matchDimensions(canvasRef.current, videoRef.current, true);
            const resizedDetection = faceapi.resizeResults(detection, dims);
            
            canvasRef.current.getContext('2d').clearRect(0, 0, dims.width, dims.height);
            faceapi.draw.drawDetections(canvasRef.current, resizedDetection);
            faceapi.draw.drawFaceLandmarks(canvasRef.current, resizedDetection);
          }
          
          // Liveness check - blink detection
          if (livenessCheckEnabled && livenessStep === 'blink' && !blinkDetected) {
            const landmarks = detection.landmarks;
            const leftEye = landmarks.getLeftEye();
            const rightEye = landmarks.getRightEye();
            
            const leftEAR = calculateEAR(leftEye);
            const rightEAR = calculateEAR(rightEye);
            const avgEAR = (leftEAR + rightEAR) / 2;
            
            // Track eye open/closed state
            const isEyeOpen = avgEAR > 0.21; // Threshold for eye being open
            
            setEyeOpenHistory(prev => {
              const newHistory = [...prev, isEyeOpen].slice(-10);
              
              // Detect blink pattern: open -> closed -> open
              if (newHistory.length >= 4) {
                const lastFour = newHistory.slice(-4);
                // Check for pattern: open, closed, closed, open
                if (lastFour[0] && !lastFour[1] && !lastFour[2] && lastFour[3]) {
                  setBlinkDetected(true);
                  setLivenessStep('done');
                  toast.success('Blink detected! Face is live.');
                }
              }
              
              return newHistory;
            });
          }
        } else {
          setFaceDetected(false);
          if (canvasRef.current) {
            canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
        }
      }
    }, 150);
  };

  // Verify face
  const verifyFace = async () => {
    if (!videoRef.current || !faceDetected) return;
    
    // Check liveness if enabled
    if (livenessCheckEnabled && !blinkDetected) {
      toast.error('Please blink to verify you are a real person');
      return;
    }
    
    setVerifying(true);
    try {
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      if (detection) {
        const response = await axios.post(`${API}/face/verify`, {
          face_descriptor: Array.from(detection.descriptor)
        });
        
        setVerificationResult(response.data);
        
        if (response.data.verified) {
          toast.success('Face verified successfully!');
          onVerificationComplete?.(true, response.data.confidence);
          setTimeout(() => handleClose(), 1500);
        } else {
          toast.error('Face does not match. Please try again.');
          onVerificationComplete?.(false, response.data.confidence);
        }
      } else {
        toast.error('No face detected. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying face:', error);
      if (error.response?.status === 404) {
        toast.error('No face enrolled. Please enroll your face first.');
      } else {
        toast.error(error.response?.data?.detail || 'Face verification failed');
      }
      onVerificationComplete?.(false, 0);
    } finally {
      setVerifying(false);
    }
  };

  // Handle close
  const handleClose = () => {
    stopCamera();
    setVerificationResult(null);
    setLivenessStep('waiting');
    setBlinkDetected(false);
    setEyeOpenHistory([]);
    onOpenChange(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-600" />
            Face Verification
          </DialogTitle>
          <DialogDescription>
            {livenessCheckEnabled 
              ? "Look at the camera and blink once to verify you're a real person."
              : "Position your face in the camera to verify your identity."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Loading models */}
          {loadingModels && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <p className="mt-2 text-sm text-slate-500">Loading face recognition...</p>
            </div>
          )}

          {/* Video preview */}
          {cameraActive && (
            <div className="relative">
              <div className="relative rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '4/3', minHeight: '300px' }}>
                <video 
                  ref={videoRef} 
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  playsInline
                  style={{ transform: 'scaleX(-1)', minHeight: '300px' }}
                />
                <canvas 
                  ref={canvasRef} 
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  style={{ transform: 'scaleX(-1)' }}
                />
                
                {/* Face detection indicator */}
                <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium ${
                  faceDetected 
                    ? 'bg-green-500 text-white' 
                    : 'bg-yellow-500 text-white'
                }`}>
                  {faceDetected ? (
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Face Detected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Looking for face...
                    </span>
                  )}
                </div>
                
                {/* Liveness indicator */}
                {livenessCheckEnabled && (
                  <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-medium ${
                    blinkDetected 
                      ? 'bg-green-500 text-white' 
                      : 'bg-orange-500 text-white animate-pulse'
                  }`}>
                    {blinkDetected ? (
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Live!
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        Please blink
                      </span>
                    )}
                  </div>
                )}

                {/* Verification result overlay */}
                {verificationResult && (
                  <div className={`absolute inset-0 flex items-center justify-center ${
                    verificationResult.verified 
                      ? 'bg-green-500/80' 
                      : 'bg-red-500/80'
                  }`}>
                    <div className="text-center text-white">
                      {verificationResult.verified ? (
                        <CheckCircle className="w-16 h-16 mx-auto mb-2" />
                      ) : (
                        <XCircle className="w-16 h-16 mx-auto mb-2" />
                      )}
                      <p className="text-xl font-bold">
                        {verificationResult.verified ? 'Verified!' : 'Not a Match'}
                      </p>
                      <p className="text-sm opacity-90">
                        Confidence: {Math.round(verificationResult.confidence * 100)}%
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Liveness instructions */}
              {livenessCheckEnabled && !blinkDetected && faceDetected && (
                <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center gap-2 text-orange-700">
                    <Eye className="w-5 h-5" />
                    <p className="text-sm font-medium">
                      Please blink naturally while looking at the camera
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-4 flex justify-center gap-3">
                <Button 
                  onClick={handleClose}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={verifyFace}
                  disabled={!faceDetected || verifying || (livenessCheckEnabled && !blinkDetected)}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="verify-btn"
                >
                  {verifying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Verify Face
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FaceVerification;
